import Auth from "../common/auth/auth";

export interface SessionWarningConfig {
    warningThresholdMinutes: number; // When to show warning (5 minutes before expiration)
    checkIntervalSeconds: number; // How often to check expiration (every 30 seconds)
}

export interface SessionWarningCallbacks {
    onWarning: (minutesRemaining: number) => void;
    onExpired: () => void;
    onRenewed: () => void;
}

export class SessionManager {
    private checkInterval: NodeJS.Timeout | null = null;
    private isWarningShown = false;
    private config: SessionWarningConfig;
    private callbacks: SessionWarningCallbacks;
    private auth: Auth;
    private visibilityHandler: (() => void) | null = null;
    private storageHandler: ((e: StorageEvent) => void) | null = null;

    constructor(
        config: SessionWarningConfig = {
            warningThresholdMinutes: 5,
            checkIntervalSeconds: 30
        },
        callbacks: SessionWarningCallbacks,
        auth: Auth
    ) {
        this.config = config;
        this.callbacks = callbacks;
        this.auth = auth;
    }

    /**
     * Starts monitoring the session expiration
     */
    public startMonitoring(): void {
        if (this.checkInterval) {
            this.stopMonitoring();
        }

        this.visibilityHandler = () => {
            if (document.visibilityState === "visible") {
                this.checkSessionExpiration();
            }
        };
        document.addEventListener("visibilitychange", this.visibilityHandler);

        this.storageHandler = (e) => {
            if (e.key === "expires_at") {
                // Another tab renewed. If this tab had the warning modal up and the fresh
                // expiry is now beyond the warning threshold, checkSessionExpiration will
                // not fire any callback on its own, leaving the modal stuck with a stale
                // countdown. Call onRenewed explicitly in that case so the hook closes it.
                const wasWarningShown = this.isWarningShown;
                this.isWarningShown = false;
                this.checkSessionExpiration();
                if (wasWarningShown && !this.isWarningShown) {
                    this.callbacks.onRenewed();
                }
            }
        };
        window.addEventListener("storage", this.storageHandler);

        this.checkInterval = setInterval(() => {
            this.checkSessionExpiration();
        }, this.config.checkIntervalSeconds * 1000);

        // Initial check
        this.checkSessionExpiration();
    }

    /**
     * Stops monitoring the session expiration
     */
    public stopMonitoring(): void {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
        if (this.visibilityHandler) {
            document.removeEventListener("visibilitychange", this.visibilityHandler);
            this.visibilityHandler = null;
        }
        if (this.storageHandler) {
            window.removeEventListener("storage", this.storageHandler);
            this.storageHandler = null;
        }
        this.isWarningShown = false;
    }

    /**
     * Checks the current session expiration status
     */
    private checkSessionExpiration(): void {
        const expiresAtVal = localStorage.getItem("expires_at");

        if (!expiresAtVal) {
            // No expiration time found, session might be invalid
            this.callbacks.onExpired();
            return;
        }

        const expiresAt = parseInt(expiresAtVal);
        const now = new Date().getTime() / 1000; // Convert to seconds
        const timeUntilExpiration = expiresAt - now;
        const minutesUntilExpiration = timeUntilExpiration / 60;

        if (timeUntilExpiration <= 0) {
            // Session has expired
            this.stopMonitoring();
            this.callbacks.onExpired();
            return;
        }

        if (minutesUntilExpiration <= this.config.warningThresholdMinutes && !this.isWarningShown) {
            // Show warning
            this.isWarningShown = true;
            this.callbacks.onWarning(Math.ceil(minutesUntilExpiration));
        }
    }

    /**
     * Attempts to renew the session token
     */
    public async renewSession(): Promise<boolean> {
        try {
            await this.auth.handleTokenRenew();
            this.isWarningShown = false; // Reset warning state
            this.callbacks.onRenewed();
            return true;
        } catch (error) {
            console.error("Failed to renew session:", error);
            this.callbacks.onExpired();
            return false;
        }
    }

    /**
     * Resets the warning state (useful when user manually interacts with the app)
     */
    public resetWarningState(): void {
        this.isWarningShown = false;
    }

    /**
     * Gets the current session time remaining in minutes
     */
    public getTimeRemainingMinutes(): number | null {
        const expiresAtVal = localStorage.getItem("expires_at");

        if (!expiresAtVal) {
            return null;
        }

        const expiresAt = parseInt(expiresAtVal);
        const now = new Date().getTime() / 1000;
        const timeUntilExpiration = expiresAt - now;

        return Math.max(0, timeUntilExpiration / 60);
    }

    /**
     * Checks if the session is currently valid
     */
    public isSessionValid(): boolean {
        const expiresAtVal = localStorage.getItem("expires_at");

        if (!expiresAtVal) {
            return false;
        }

        const expiresAt = parseInt(expiresAtVal);
        const now = new Date().getTime() / 1000;

        return expiresAt > now;
    }
}